'use client';

import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';
import { Breadcrumb, type BreadcrumbItem } from '@//components/Breadcrumb';
import { Container } from '@//components/Container';
import type { ElasticSearchResponseFacet } from '@//ElasticSearch';
import { useMarket, useMarketMetadata } from '@//providers/MarketSettingsProvider';
import styles from './BreadcrumbContainer.module.scss';
import { getBreadcrumbs } from './utils';

interface BreadcrumbContainerProps {
  extra?: BreadcrumbItem[];
  className?: string;
  testId?: string;
  vehicleData?: { manufacturer?: string; modelSeries?: string };
  facets?: ElasticSearchResponseFacet[];
  searchSubscriptionName?: string;
}

const BreadcrumbContainer: React.FC<BreadcrumbContainerProps> = ({
  extra = [],
  className,
  testId,
  vehicleData,
  facets,
  searchSubscriptionName,
}) => {
  const market = useMarket();
  const pathname = usePathname();
  const t = useTranslations('other.breadcrumbs');
  const marketMetadata = useMarketMetadata();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsSticky(entry.intersectionRatio < 1);
      },
      { threshold: [1], rootMargin: '-1px 0px 0px 0px' },
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  const _items = getBreadcrumbs(
    market,
    pathname,
    t,
    marketMetadata,
    vehicleData,
    facets,
    searchSubscriptionName,
  );
  const items = [..._items, ...extra];
  items[items.length - 1].href = undefined;

  return (
    <div
      ref={containerRef}
      className={clsx(styles.container, { [styles.containerSticky]: isSticky })}
      data-nextjs-scroll-focus-boundary
    >
      <Container size="max">
        <Breadcrumb items={items} testId={testId || 'breadcrumb'} className={className} />
      </Container>
    </div>
  );
};

export default BreadcrumbContainer;
